import React, { useCallback } from 'react';
import LoginComponent from '@/components/LoginAndDropdownComponent';
import store from 'src/redux/store';
import { IS_SERVER } from '@/utils/checkRenderEnv';
import { btnActionHandler } from '../../ModernTheme/utils/btnUtils';
import cloneDeep from 'lodash/cloneDeep';
import { getRoute, PATH } from 'src/utils/routes';
import { useGetCategoriesList } from 'src/components/WidgetMaker/WidgetDnD/WidgetProperties/HeaderBuilder/components/utils';
import { useWidgetDndContextData } from 'src/context/WidgetDndContext';
import { useSSRSelector } from 'src/redux';
import router from 'next/router';
import { isPLPRequiredSystem } from 'src/utils/isPLPRequiredSystem';
import { validateLink } from 'src/utils/validateLink';
import { checkIfUrlHasExternalLink } from 'src/utils/checkIfUrlHasExternalLink';
import { isWebsiteInternallyRouted } from 'src/utils/isWebsiteInternallyRouted';

export const logoSectionConfigDetails = (activeHeaderData, isScroll) => {
  const { headerPropsConfig } = activeHeaderData || {};
  const { logo_section, globalClass } = headerPropsConfig || {};
  const { staticLogo, scrollLogo } = logo_section || {};
  const currentType = isScroll ? scrollLogo : staticLogo;
  const isTransparent =
    globalClass?.staticClass?.base_config?.defaultFillColorType === 'transparent';
  const {
    base_config,
    componentData: { url },
  } =
    scrollLogo?.componentData?.url?.length > 0 && isTransparent
      ? currentType
      : staticLogo;
  const storeName = store.getState().storeReducer.store?.store_info?.name?.trim();
  return {
    hidden: !(base_config.showLogo || base_config.showStoreName),
    onlyLogo: base_config.showLogo && url && !(base_config.showStoreName && storeName),
    onlyStoreName:
      base_config.showStoreName && storeName && !(base_config.showLogo && url),
    logoAndName: base_config.showStoreName && storeName && base_config.showLogo && url,
  };
};

function getApproxCharLengthAllowed({
  preferredWidth = 720,
  charLengthAllowed = 56,
  maxCharactersLimit,
}) {
  const calculatedChar = Math.floor(charLengthAllowed * (preferredWidth / 720));
  return calculatedChar > maxCharactersLimit ? maxCharactersLimit : calculatedChar;
}

export function getMaxCharactersAllowedInNavLinks({
  containerRef,
  headerName,
  isBuilder,
  headerWidth,
  navlinksPosition,
  searchBarType,
}) {
  const sectionWidth = containerRef.current?.clientWidth;
  const preferredWidth = (isBuilder ? sectionWidth : headerWidth) - 18;
  let charLengthAllowed: 'unset' | number = 'unset';
  if (headerName === 'header1') {
    if (preferredWidth <= 720) {
      if (searchBarType === 'field')
        charLengthAllowed = navlinksPosition === 'left' ? 17 : 7;
      if (searchBarType === 'icon') charLengthAllowed = 21;
      if (searchBarType === 'labelIcon') charLengthAllowed = 21;
    }
    if (preferredWidth > 720 && preferredWidth < 1280) {
      if (searchBarType === 'field')
        charLengthAllowed = navlinksPosition === 'left' ? 17 : 7;
      if (searchBarType === 'icon') charLengthAllowed = 21;
      if (searchBarType === 'labelIcon') charLengthAllowed = 21;
    }
    if (preferredWidth >= 1280 && preferredWidth < 1366) {
      if (searchBarType === 'field') charLengthAllowed = 15;
      if (searchBarType === 'icon') charLengthAllowed = 40;
      if (searchBarType === 'labelIcon') charLengthAllowed = 35;
    }
    if (preferredWidth >= 1366 && preferredWidth < 1420) {
      if (searchBarType === 'field') charLengthAllowed = 17;
      if (searchBarType === 'icon') charLengthAllowed = 45;
      if (searchBarType === 'labelIcon') charLengthAllowed = 40;
    }
    if (preferredWidth >= 1420 && preferredWidth < 1500) {
      if (searchBarType === 'field') charLengthAllowed = 17;
      if (searchBarType === 'icon') charLengthAllowed = 50;
      if (searchBarType === 'labelIcon') charLengthAllowed = 45;
    }
    if (preferredWidth >= 1500 && preferredWidth <= 1800) {
      if (searchBarType === 'field') charLengthAllowed = 34;
      if (searchBarType === 'icon') charLengthAllowed = 77;
      if (searchBarType === 'labelIcon') charLengthAllowed = 64;
    }

    if (preferredWidth > 1800) {
      if (searchBarType === 'field') charLengthAllowed = 70;
      if (searchBarType === 'icon') charLengthAllowed = 92;
      if (searchBarType === 'labelIcon') charLengthAllowed = 81;
    }
  }

  if (headerName === 'header2') {
    if (preferredWidth <= 720) {
      charLengthAllowed = 16;
    }
    if (preferredWidth > 720 && preferredWidth <= 1100) {
      charLengthAllowed = 15;
    }
    if (preferredWidth > 1100 && preferredWidth <= 1500) {
      charLengthAllowed = 31;
    }
    if (preferredWidth > 1500 && preferredWidth <= 1800) {
      charLengthAllowed = 35;
    }
    if (preferredWidth > 1800) {
      charLengthAllowed = 49;
    }
  }

  if (headerName === 'header3' || headerName === 'header4') {
    if (preferredWidth > 720 && preferredWidth <= 999) {
      charLengthAllowed = 56;
      charLengthAllowed = getApproxCharLengthAllowed({
        preferredWidth,
        charLengthAllowed,
        maxCharactersLimit: 100,
      });
    }
    if (preferredWidth >= 1000 && preferredWidth <= 1262) {
      charLengthAllowed = 70;
      charLengthAllowed = getApproxCharLengthAllowed({
        preferredWidth,
        charLengthAllowed,
        maxCharactersLimit: 100,
      });
    }
    if (preferredWidth > 1262 && preferredWidth <= 1347) {
      charLengthAllowed = 70;
      charLengthAllowed = getApproxCharLengthAllowed({
        preferredWidth,
        charLengthAllowed,
        maxCharactersLimit: 104,
      });
    }
    if (preferredWidth > 1347 && preferredWidth <= 1421) {
      charLengthAllowed = 104;
      charLengthAllowed = getApproxCharLengthAllowed({
        preferredWidth,
        charLengthAllowed,
        maxCharactersLimit: 133,
      });
    }
    if (preferredWidth > 1421 && preferredWidth <= 1517) {
      charLengthAllowed = 111;
      charLengthAllowed = getApproxCharLengthAllowed({
        preferredWidth,
        charLengthAllowed,
        maxCharactersLimit: 140,
      });
    }
    if (preferredWidth > 1517 && preferredWidth <= 1581) {
      charLengthAllowed = 116;
      charLengthAllowed = getApproxCharLengthAllowed({
        preferredWidth,
        charLengthAllowed,
        maxCharactersLimit: 140,
      });
    }
    if (preferredWidth > 1581 && preferredWidth <= 1782) {
      charLengthAllowed = 132;
      charLengthAllowed = getApproxCharLengthAllowed({
        preferredWidth,
        charLengthAllowed,
        maxCharactersLimit: 160,
      });
    }
    if (preferredWidth > 1782) {
      charLengthAllowed = 140;
      charLengthAllowed = getApproxCharLengthAllowed({
        preferredWidth,
        charLengthAllowed,
        maxCharactersLimit: 170,
      });
    }
  }

  return charLengthAllowed;
}

function checkForShopComponent(linksData) {
  linksData?.forEach((data) => {
    if (data?.isShopComponent && data?.option?.length && data?.type === 'text') {
      data.type = 'dropdown';
      data.redirectionLink = '';
    }
  });

  return cloneDeep(linksData);
}

export const useGetNavlinkList = ({
  activeHeaderData,
  dynamicSectionData,
  headerNodeRef,
  isBuilder,
  componentData,
  headerName,
  containerRef,
  isMobile,
}) => {
  const headerWidth =
    headerNodeRef?.clientWidth || (!IS_SERVER ? window.innerWidth : isMobile ? 481 : 781);
  const navlinksPosition =
    activeHeaderData?.headerPropsConfig?.navLinks_section?.base_config?.defaultPosition;
  const visibleLinks = componentData?.filter((data) => data?.showOnHeader);
  const { base_config: dynamicSectionBaseConfig } = dynamicSectionData;
  const { defaultType: searchBarType } = dynamicSectionBaseConfig;

  function checkIfMoreAlreadyThere(visibleLinks) {
    let moreIndex = -1;
    for (let i = 0; i < visibleLinks?.length; i++) {
      if (visibleLinks[i]?.name?.toLowerCase() === 'more') {
        moreIndex = i;
        break;
      }
    }
    return moreIndex;
  }

  function removeMoreOption(restElements) {
    return restElements?.filter((data) => data?.name?.toLowerCase() !== 'more');
  }

  function getNavLinkSliceCount() {
    const charLengthAllowed = getMaxCharactersAllowedInNavLinks({
      containerRef,
      headerName,
      isBuilder,
      headerWidth,
      navlinksPosition,
      searchBarType,
    });

    let sliceCount = 0;
    if (charLengthAllowed === 'unset') {
      return sliceCount;
    }
    let currentCharacterCount = 0;
    for (let currentIndex = 0; currentIndex <= visibleLinks.length - 1; currentIndex++) {
      currentCharacterCount =
        currentCharacterCount + visibleLinks[currentIndex].name.length;
      if (currentCharacterCount > charLengthAllowed) {
        break;
      } else {
        ++sliceCount;
      }
    }

    return sliceCount;
  }

  const getNavLinkItems = useCallback(() => {
    let sliceCount = 1;

    sliceCount = getNavLinkSliceCount();

    let newComponentData = [];
    if (sliceCount > visibleLinks?.length) {
      newComponentData = visibleLinks;
    } else {
      const currentData = visibleLinks?.slice(0, sliceCount);
      const restElements = visibleLinks?.slice(sliceCount, visibleLinks?.length);
      let newData = [];
      const moreIndex = checkIfMoreAlreadyThere(visibleLinks);
      if (restElements?.length) {
        newData = {
          name: 'More',
          type: 'dropdown',
          option:
            moreIndex >= 0
              ? [...removeMoreOption(restElements), ...visibleLinks?.[moreIndex]?.option]
              : [...restElements],
          showOnHeader: true,
          elementLevelClass: '',
        } as any;
      }
      newComponentData = [
        ...(restElements?.length > 0 ? removeMoreOption(currentData) : currentData),
        newData,
      ];
    }
    return newComponentData;
  }, [headerNodeRef, visibleLinks]);

  const updatedNavLinks = checkForShopComponent(getNavLinkItems());
  return { navlinkList: updatedNavLinks };
};

export const getHamburgerCustomList = (
  activeHeaderData,
  onMenuDrawerItemClick,
  closeMenuDrawerOnMobile,
  canShowItem
) => {
  const drawerCustomList =
    activeHeaderData.headerPropsConfig.navIcons_section?.componentData?.iconsList
      ?.filter((item) => item.visibility?.hamburger === true)
      .reverse()
      .map((item) => {
        return canShowItem(item) && item.name === 'loginLogout' ? (
          <LoginComponent disableDropdown>
            <div>{item.label}</div>
          </LoginComponent>
        ) : (
          <p
            key={item.label}
            className="tw-m-0 "
            onClick={() =>
              onMenuDrawerItemClick(item.action.actionType, item.action.actionData)
            }
          >
            {item.label}
          </p>
        );
      }) || [];

  const visibleButtons = activeHeaderData?.headerPropsConfig?.buttons?.filter(
    (item) => !!item?.isShow && !!item?.text?.trim()
  );

  const buttonList =
    visibleButtons?.map((item) => {
      return (
        <p
          key={item.text}
          className="tw-m-0 "
          onClick={() => {
            closeMenuDrawerOnMobile();
            btnActionHandler(item?.actions, () => {});
          }}
        >
          {item.text}
        </p>
      );
    }) || [];

  return drawerCustomList?.concat(buttonList) || [];
};

/**
 * webFrame is the route which only has CFE header and the body is loaded in iFrame
 * @returns if the route is an iFrame route
 */
export const isWebFrameRoute = () => {
  if (IS_SERVER) {
    return false;
  }
  return window.location.pathname.includes('/webFrame');
};

/**
 *
 * @param redirectionLink
 * @param navigationList
 * @returns updated slug URL
 */
export function getRedirectLinkForSlug(navigationList = [], redirectionLink, id) {
  const DEFAULT_PAGE_TYPES = ['social_media', 'faqs', 'testimonial'];

  // If the link is blog type, redirect it to /blog URL
  const matchingNavItem = navigationList.find((navItem) => navItem.page_id === id);
  if (matchingNavItem?.page_type === 'ai-blog' || redirectionLink?.includes('/blog')) {
    return PATH.BLOG;
  }

  // If the link is of type DEFAULT_PAGE_TYPES, then append /p infront of URL if not already appended.
  if (
    DEFAULT_PAGE_TYPES.includes(matchingNavItem?.page_type) &&
    !redirectionLink?.includes('p/')
  ) {
    return `${PATH.additionalPage(redirectionLink)}`;
  }

  return redirectionLink;
}

export const useGetDropdownListforShopComponent = () => {
  const {
    widgetContextState: { isBuilder },
  } = useWidgetDndContextData();
  const categoriesList = useGetCategoriesList();

  function getShopComponentLinks(option) {
    const categories = Object.values(categoriesList) as any[];
    // Updating details of category item (such as name)
    categories.forEach((newOption) => {
      const oldItemIdx = option.findIndex((oldItem) => oldItem.id === newOption.id);
      if (oldItemIdx === -1) return null;
      option[oldItemIdx] = {
        ...option[oldItemIdx],
        name: newOption.name,
      };
    });
    // Extracting newly added categories
    const newCatList = categories?.filter((item: any) => {
      const isOldCat = !!option.find(
        (cat) => (item.id && cat.id === item.id) || cat.name === item.name
      );
      return !isOldCat;
    });

    return option
      ?.concat(newCatList)
      .filter((item) => item.showOnHeader !== false && item.showOnHeader !== 0);
  }

  function getDropdownList(isShopComponent, options = []) {
    const option = options || [];
    if (isBuilder) {
      return option;
    }
    if (!isShopComponent) {
      return option.filter(
        (item) => item.showOnHeader !== false && item.showOnHeader !== 0
      );
    }
    const shopLinks = getShopComponentLinks(options);
    return shopLinks;
  }

  return { getDropdownList, categoriesList };
};

export const useHeaderLinksUtils = () => {
  const { store, navigationList } = useSSRSelector((state) => ({
    store: state.storeReducer.store,
    navigationList: state.additionalPagesReducer.navigationList || [],
  }));
  const { getDropdownList, categoriesList } = useGetDropdownListforShopComponent();
  const domainName = store?.store_info?.domain;

  /* We have found cases where merchants type in https://domain.in/path, www.domain.in/path, domain.in/path in link section.
   * This we will now extract path from them */
  function extractPathFromInternalLink(link) {
    if (IS_SERVER) return link;
    if (isWebsiteInternallyRouted(window.location.host)) return link;
    let redirectLink = link;
    if (domainName && redirectLink?.includes(domainName)) {
      redirectLink = redirectLink?.split(domainName)[1];
    }
    return redirectLink;
  }

  // Curries a function with pre-added argument to remove redundant code writing
  function getRouteLink(link) {
    return getRoute(link, domainName);
  }

  function getCategoryLink(currentCategory) {
    return getRouteLink(
      `${
        isPLPRequiredSystem(store?.theme?.theme_class) ? PATH.SHOP : PATH.PRODUCT
      }?cid=${currentCategory?.id || ''}`
    );
  }

  // Read function doc, in short, this handles additional pages linking
  const getAdditionalPagesRedirectLink = (linkInfo) => {
    return getRedirectLinkForSlug(
      navigationList,
      validateLink(linkInfo?.redirectionLink),
      linkInfo?.id
    );
  };

  // Used for final redirections, no logic takes place here
  const onCustomNavItemClick = ({
    link,
    openInNewTab,
    isTypeDropdown,
  }: {
    link?: string;
    openInNewTab?: boolean;
    isTypeDropdown?: boolean;
  }) => {
    if (isTypeDropdown) return;

    if (openInNewTab) {
      window.open(link, '_blank');
      return;
    }

    router.push(getRouteLink(link || '/'));
  };

  // Main redirection logic handler -> takes in a link object that can be standalone or nested in a dropdown
  const handleHeaderNavItemClick = (linkInfo) => {
    if (!linkInfo) return;

    const { isShopComponent, redirectNewPage: openInNewTab } = linkInfo;

    /**
     * 1. Only unique case is that if that link is a child of a Shop dropdown,
     * 2. In that case we are sure the dropdown will contain categories & tags, thus we handle that.
     * 3. When collection are present, we use mega menu in desktop, but in desktop there is two layer nesting, Shop -> Collection (non-clickable) -> Category (clickable)
     */
    if (isShopComponent) {
      const currentCategory = categoriesList?.find(
        (val) => val?.id === linkInfo?.id || val?.name === linkInfo?.name
      );
      if (currentCategory?.id) {
        router.push(getCategoryLink(currentCategory), undefined, { shallow: true });
      }
      return;
    }

    const redLink = getAdditionalPagesRedirectLink(linkInfo);
    let link = extractPathFromInternalLink(validateLink(redLink));
    const isExternalLinkPresent = checkIfUrlHasExternalLink(link);
    if (openInNewTab && !isExternalLinkPresent) link = getRouteLink(link);

    onCustomNavItemClick({ link, openInNewTab: isExternalLinkPresent || openInNewTab });
  };

  /** This returns an inline link that is passed in anchor tag, so that the link is accessible & has
   * link like properties that are recognised by the browser.
   * Same react component can both handleHeaderNavItemClick & this function
   * p.s. -> external links are by default opened in new tab
   *  */
  type ReturnHeaderNavLink = { link: string; target?: React.HTMLAttributeAnchorTarget };
  const getHeaderNavInlineLink = (linkInfo): ReturnHeaderNavLink => {
    if (linkInfo.isShopComponent) {
      return { link: getCategoryLink(linkInfo) };
    }

    const redirectLink = getAdditionalPagesRedirectLink(linkInfo);
    const isExternalLinkPresent = checkIfUrlHasExternalLink(redirectLink);

    return {
      link: extractPathFromInternalLink(
        !isExternalLinkPresent ? getRouteLink(redirectLink) : redirectLink
      ),
      target: isExternalLinkPresent ? '_blank' : undefined,
    };
  };

  function generatePathForInternalRedirection(linkInfo) {
    const redirectLink = getAdditionalPagesRedirectLink(linkInfo);
    return extractPathFromInternalLink(redirectLink);
  }

  return {
    handleHeaderNavItemClick,
    getDropdownList,
    getHeaderNavInlineLink,
    onCustomNavItemClick,
    generatePathForInternalRedirection,
  };
};
